import { Request } from 'Core/Common/Request';
import { P } from 'Core/Common/Promise';
import { CookieManager } from 'Core/Common/CookieManager';
import type { IfcParseResultDto } from 'api/IfcParser/Application/Models/IfcParseResultDto';
import type { IfcNodeDto } from 'api/IfcParser/Application/Models/IfcNodeDto';
import type { IfcExistingRecordsDto } from 'api/IfcParser/Application/Models/IfcExistingRecordsDto';

export type { IfcParseResultDto } from 'api/IfcParser/Application/Models/IfcParseResultDto';
export type { IfcNodeDto } from 'api/IfcParser/Application/Models/IfcNodeDto';
export type { IfcPropertySetDto } from 'api/IfcParser/Application/Models/IfcPropertySetDto';
export type { IfcPropertyDto } from 'api/IfcParser/Application/Models/IfcPropertyDto';
export type { IfcQuantitySetDto } from 'api/IfcParser/Application/Models/IfcQuantitySetDto';
export type { IfcQuantityDto } from 'api/IfcParser/Application/Models/IfcQuantityDto';
export type { IfcMaterialDto } from 'api/IfcParser/Application/Models/IfcMaterialDto';
export type { IfcExistingRecordsDto } from 'api/IfcParser/Application/Models/IfcExistingRecordsDto';

export interface IfcImportResultDto {
	created: number;
	skipped: number;
	failed: number;
	items: IfcImportItemResultDto[];
}

export interface IfcImportItemResultDto {
	expressId: number;
	status: string;
	recordId: number | null;
	error: string | null;
}

const CHUNK_SIZE = 1024 * 1024; // 1 MB
const CAMEL_CASE_HEADERS = { 'Accept-CamelCase': 'true' };

export interface ParseStreamCallbacks {
	onNode: (node: IfcNodeDto) => void;
	onComplete: (result: IfcParseResultDto) => void;
	onError: (message: string) => void;
	onProgress?: (percent: number, message: string) => void;
}

/**
 * Shared IFC API client. Wraps every endpoint under /api/IfcApi/* plus the
 * file-chunk uploader. Used by the IFC importer page (full-import flow) and
 * the IFC viewer (server-side tree parsing for arbitrary IFC bytes).
 */
export class IfcApi {

	static async Upload(file: File, onProgress?: (percent: number, message: string) => void): Promise<void> {
		const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

		for (let i = 0; i < totalChunks; i++) {
			const start = i * CHUNK_SIZE;
			const end = Math.min(start + CHUNK_SIZE, file.size);
			const chunk = file.slice(start, end);

			const formData = new FormData();
			formData.append('file', chunk, file.name);

			const percent = Math.round(((i + 1) / totalChunks) * 80);
			onProgress?.(percent, `Uploading... ${i + 1}/${totalChunks}`);

			await new Promise<void>((resolve, reject) => {
				Request.Send({
					proxy: {
						url: '/api/FilesApi/UploadFileChunk',
						type: 'POST',
						contentType: false,
						processData: false,
						dataType: 'text'
					},
					params: formData
				}).then(() => resolve()).fail(reject);
			});
		}
	}

	static async ParseStream(fileName: string, callbacks: ParseStreamCallbacks): Promise<void> {
		const url = `/api/IfcApi/ParseStream?fileName=${encodeURIComponent(fileName)}`;
		const token = CookieManager.GetAuthToken();
		const response = await fetch(url, {
			headers: { Authorization: `Bearer ${token}` }
		});
		await IfcApi.consumeSseParse(response, callbacks);
	}

	/**
	 * Same SSE event protocol as ParseStream, but the source IFC bytes are
	 * resolved server-side from a record's DOCUMENT field instead of a
	 * pre-uploaded temp file. Used by the IFC viewer to skip a redundant
	 * download → re-upload round trip when the file is already in the DB.
	 */
	static async ParseRecordStream(
		request: { fieldId: number; recordKeys: Array<{ fieldId: number; recordId: number }> },
		callbacks: ParseStreamCallbacks
	): Promise<void> {
		const token = CookieManager.GetAuthToken();
		const response = await fetch('/api/IfcApi/ParseRecordStream', {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${token}`,
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(request),
		});
		await IfcApi.consumeSseParse(response, callbacks);
	}

	/**
	 * Reads the SSE stream from one of the parse endpoints, decoding `node`,
	 * `complete` and `error` events into the provided callbacks.
	 */
	private static async consumeSseParse(
		response: Response,
		callbacks: ParseStreamCallbacks
	): Promise<void> {
		if (!response.ok) {
			callbacks.onError(`Server error: ${response.status}`);
			return;
		}
		if (!response.body) {
			callbacks.onError('Server returned no body');
			return;
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = '';

		while (true) {
			const { done, value } = await reader.read();
			if (done) break;

			buffer += decoder.decode(value, { stream: true });

			const parts = buffer.split('\n\n');
			// split() always returns at least one element, so pop() never
			// returns undefined here — fall back to '' to satisfy the type.
			buffer = parts.pop() ?? '';

			for (const part of parts) {
				let event = '';
				let data = '';
				for (const line of part.split('\n')) {
					if (line.startsWith('event: ')) event = line.slice(7);
					else if (line.startsWith('data: ')) data = line.slice(6);
				}

				if (event === 'node') {
					callbacks.onNode(JSON.parse(data));
				} else if (event === 'complete') {
					callbacks.onComplete(JSON.parse(data));
				} else if (event === 'error') {
					callbacks.onError(data);
				}
			}
		}
	}

	static Import(
		nodes: IfcNodeDto[],
		tableName: string = 'ARTICLES',
		defaultWarehouseId: number | null = null,
		classWarehouseMappings: Record<string, number> = {},
		fileName: string | null = null
	): P.Promise<IfcImportResultDto> {
		return Request.Send({
			proxy: {
				url: '/api/IfcApi/Import',
				type: 'POST',
				contentType: 'application/json',
				headers: CAMEL_CASE_HEADERS
			},
			params: JSON.stringify({ tableName, nodes, defaultWarehouseId, classWarehouseMappings, fileName })
		});
	}

	static GetWarehouses(): P.Promise<Array<{ id: number; name: string }>> {
		return Request.Send({
			proxy: {
				url: '/api/IfcApi/GetWarehouses',
				type: 'GET',
				headers: CAMEL_CASE_HEADERS
			}
		});
	}

	static GetWarehousesEntityId(): P.Promise<{ entityId: number }> {
		return Request.Send({
			proxy: {
				url: '/api/IfcApi/GetWarehousesEntityId',
				type: 'GET',
				headers: CAMEL_CASE_HEADERS
			}
		});
	}

	static GetWarehouseClassMappings(): P.Promise<{ mappings: Record<string, number> }> {
		return Request.Send({
			proxy: {
				url: '/api/IfcApi/GetWarehouseClassMappings',
				type: 'GET',
				// cache-bust: the modal re-reads this right after a save; a cached
				// GET (jQuery defaults to cache:true) serves the pre-save mapping.
				cache: false,
				headers: CAMEL_CASE_HEADERS
			}
		});
	}

	static SaveWarehouseClassMappings(classes: string[], mappings: Record<string, number>): P.Promise<{ saved: boolean }> {
		return Request.Send({
			proxy: {
				url: '/api/IfcApi/SaveWarehouseClassMappings',
				type: 'POST',
				contentType: 'application/json',
				headers: CAMEL_CASE_HEADERS
			},
			params: JSON.stringify({ classes, mappings })
		});
	}

	static FindExisting(globalIds: string[], tableName: string = 'ARTICLES'): P.Promise<IfcExistingRecordsDto> {
		return Request.Send({
			proxy: {
				url: '/api/IfcApi/FindExisting',
				type: 'POST',
				contentType: 'application/json',
				headers: CAMEL_CASE_HEADERS
			},
			params: JSON.stringify({ tableName, globalIds })
		});
	}

	static GenerateSvg(fileName: string, globalId?: string): P.Promise<{ svg: string; globalId: string | null }> {
		const url = `/api/IfcApi/GenerateSvg?fileName=${encodeURIComponent(fileName)}${globalId ? `&globalId=${encodeURIComponent(globalId)}` : ''}`;
		return Request.Send({
			proxy: {
				url,
				type: 'GET',
				headers: CAMEL_CASE_HEADERS
			}
		});
	}

	static GetBsddClassInfo(ifcClass: string, ifcVersion: string = '4.3'): P.Promise<any> {
		const url = `/api/IfcApi/GetBsddClassInfo?ifcClass=${encodeURIComponent(ifcClass)}&ifcVersion=${encodeURIComponent(ifcVersion)}`;
		return Request.Send({
			proxy: {
				url,
				type: 'GET',
				headers: CAMEL_CASE_HEADERS
			}
		});
	}

	static Cleanup(fileName: string): P.Promise<void> {
		return Request.Send({
			proxy: {
				url: '/api/IfcApi/Cleanup',
				type: 'POST',
				contentType: 'application/json',
				headers: CAMEL_CASE_HEADERS
			},
			params: JSON.stringify({ fileName })
		});
	}
}
